// src/pages/ProductDetails.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getProductBySlug } from "../services/publicProductService";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";
import "./ProductDetails.css";

/* ---------------- Helpers ---------------- */
const SITE_NAME = "GOD'S GIFT FASHION";
const norm = (s) => (s ?? "").toString().trim().toLowerCase();
const truncate = (s = "", n = 160) => (s || "").replace(/\s+/g, " ").trim().slice(0, n);

const absUrl = (path = "") => {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return new URL(path, base).toString();
  } catch {
    return path;
  }
};

const fallbackImg = (w = 1000, h = 750, t = "Image") =>
  `https://via.placeholder.com/${w}x${h}?text=${encodeURIComponent(t)}`;

const availabilityForSchema = (inStock) =>
  inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";

const numberFromText = (t) => {
  if (t == null) return null;
  if (typeof t === "number") return t;
  const s = String(t).replace(/\s/g, "").replace(/[^\d.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const formatXof = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  // 👉 Affiche: 5000 FCFA (sans séparateur)
  return `${Math.round(num)} FCFA`;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const clampScale = (s) => clamp(s, 1, 4);

/* ---------------- Price (FIXED) ---------------- */
function PriceBlock({ product }) {
  const promoColor = "#ff9800";

  // Prix normal: peut venir de priceText / compareAtPrice / price
  const normalText = product?.priceText ?? product?.compareAtPrice ?? product?.price;
  const normal = numberFromText(normalText);

  // Prix promo: uniquement si un champ promo existe vraiment
  const promoCandidateText = product?.priceTextPromo; // ✅ on ne fallback PAS sur product.price
  const promo = numberFromText(promoCandidateText);

  // Promo valide seulement si promo existe ET >0 ET normal existe ET >0 ET promo < normal
  const hasPromo =
    promo != null &&
    normal != null &&
    promo > 0 &&
    normal > 0 &&
    promo < normal;

  return (
    <div className="pd-price">
  <div className="pd-price-main" style={{ color: promoColor }}>
    {hasPromo ? formatXof(promo) : formatXof(normal ?? 0)}
  </div>

  {hasPromo && (
    <div className="pd-price-old">
      <span className="text-decoration-line-through">
        {formatXof(normal)}
      </span>
    </div>
  )}
</div>
  );
}

/* ---------------- Zoomable Image (NO MODAL) ---------------- */
function AlibabaZoom({ src, alt, onErrorSrc, onPrev, onNext }) {
  const wrapRef = useRef(null);

  const isCoarse = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(pointer: coarse)").matches;
  }, []);

  // Desktop hover zoom
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  // Mobile pinch/drag zoom
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const panRef = useRef({
    dragging: false,
    x0: 0,
    y0: 0,
    tx0: 0,
    ty0: 0,
    swiping: false,
    sx0: 0,
    sy0: 0,
  });

  const pinchRef = useRef({ active: false, d0: 0, s0: 1 });

  const resetTouch = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  useEffect(() => {
    setHover(false);
    setPos({ x: 50, y: 50 });
    resetTouch();
  }, [src, resetTouch]);

  const updateFromClient = useCallback((clientX, clientY) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((clientX - r.left) / r.width) * 100;
    const y = ((clientY - r.top) / r.height) * 100;
    setPos({ x: clamp(x, 0, 100), y: clamp(y, 0, 100) });
  }, []);

  const onMouseEnter = useCallback(
    (e) => {
      if (isCoarse) return;
      setHover(true);
      updateFromClient(e.clientX, e.clientY);
    },
    [isCoarse, updateFromClient]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (!hover || isCoarse) return;
      updateFromClient(e.clientX, e.clientY);
    },
    [hover, isCoarse, updateFromClient]
  );

  const onMouseLeave = useCallback(() => setHover(false), []);

  const onPointerDown = useCallback(
    (e) => {
      if (!isCoarse) return;

      panRef.current.dragging = true;
      panRef.current.x0 = e.clientX;
      panRef.current.y0 = e.clientY;
      panRef.current.tx0 = tx;
      panRef.current.ty0 = ty;

      panRef.current.swiping = true;
      panRef.current.sx0 = e.clientX;
      panRef.current.sy0 = e.clientY;

      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [isCoarse, tx, ty]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!isCoarse) return;
      if (!panRef.current.dragging) return;

      const dx = e.clientX - panRef.current.x0;
      const dy = e.clientY - panRef.current.y0;

      if (scale > 1) {
        setTx(panRef.current.tx0 + dx);
        setTy(panRef.current.ty0 + dy);
      }
    },
    [isCoarse, scale]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!isCoarse) return;
      if (!panRef.current.dragging) return;

      panRef.current.dragging = false;

      if (scale <= 1 && panRef.current.swiping) {
        const dx = e.clientX - panRef.current.sx0;
        const dy = e.clientY - panRef.current.sy0;

        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          dx < 0 ? onNext?.() : onPrev?.();
        }
      }

      panRef.current.swiping = false;
    },
    [isCoarse, scale, onNext, onPrev]
  );

  const dist = (t1, t2) => {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = useCallback(
    (e) => {
      if (!isCoarse) return;
      if (e.touches?.length === 2) {
        pinchRef.current.active = true;
        pinchRef.current.d0 = dist(e.touches[0], e.touches[1]);
        pinchRef.current.s0 = scale;
      }
    },
    [isCoarse, scale]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!isCoarse) return;
      if (!pinchRef.current.active || e.touches?.length !== 2) return;
      e.preventDefault();

      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / (pinchRef.current.d0 || d);
      const ns = clampScale(Number((pinchRef.current.s0 * ratio).toFixed(2)));
      setScale(ns);

      if (ns <= 1) {
        setTx(0);
        setTy(0);
      }
    },
    [isCoarse]
  );

  const onTouchEnd = useCallback(
    (e) => {
      if (!isCoarse) return;
      if (e.touches?.length < 2) pinchRef.current.active = false;
    },
    [isCoarse]
  );

  return (
    <div
      ref={wrapRef}
      className={`pd-alibaba ${hover ? "is-hover" : ""} ${isCoarse ? "is-coarse" : ""}`}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      role="group"
      aria-label="Image produit avec zoom type Alibaba"
    >
      <img
        src={src}
        alt={alt}
        className="pd-alibaba-img"
        onError={(e) => (e.currentTarget.src = onErrorSrc)}
        draggable={false}
        decoding="async"
        loading="eager"
        style={
          isCoarse ? { transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})` } : undefined
        }
      />

      {!isCoarse && hover && (
        <>
          <div className="pd-lens" style={{ left: `${pos.x}%`, top: `${pos.y}%` }} aria-hidden="true" />
          <div
            className="pd-zoom-panel"
            aria-hidden="true"
            style={{
              backgroundImage: `url("${src}")`,
              backgroundPosition: `${pos.x}% ${pos.y}%`,
            }}
          />
        </>
      )}

      <div className="pd-alibaba-hint" aria-hidden="true">
        {!isCoarse
          ? "Survolez pour zoom"
          : scale > 1
            ? "Glissez pour déplacer"
            : "Glissez pour changer d'image · Pincez pour zoom"}
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function ProductDetails() {
  const { slug } = useParams();
  const { addItem } = useCart();

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const prod = await getProductBySlug(slug);
      setP(prod || null);
      setActiveImg(0);
      setSize("");
      setColor("");
      setQty(1);
      setLoading(false);
    })();
  }, [slug]);

  const variants = useMemo(() => (Array.isArray(p?.variants) ? p.variants : []), [p]);
  const vnorm = useMemo(
    () =>
      variants.map((v) => ({
        size: norm(v.size),
        color: norm(v.color),
        stock: Number.isFinite(v.stock) ? v.stock : Number(p?.stock || 0),
      })),
    [variants, p]
  );

  const sizeLabels = useMemo(() => {
    const map = new Map();
    variants.forEach((v) => {
      if (v.size) {
        const k = norm(v.size);
        if (!map.has(k)) map.set(k, v.size);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [variants]);

  const colorLabels = useMemo(() => {
    const map = new Map();
    variants.forEach((v) => {
      if (v.color) {
        const k = norm(v.color);
        if (!map.has(k)) map.set(k, v.color);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [variants]);

  const selectedSize = norm(size);
  const selectedColor = norm(color);

  const sizeOptions = useMemo(() => {
    if (sizeLabels.length === 0) return [];
    return sizeLabels.map(({ value, label }) => {
      const ok = vnorm.some(
        (v) =>
          v.size === value &&
          (selectedColor ? v.color === selectedColor : true) &&
          (v.stock ?? 0) > 0
      );
      return { value, label, disabled: !ok };
    });
  }, [sizeLabels, vnorm, selectedColor]);

  const colorOptions = useMemo(() => {
    if (colorLabels.length === 0) return [];
    return colorLabels.map(({ value, label }) => {
      const ok = vnorm.some(
        (v) =>
          v.color === value &&
          (selectedSize ? v.size === selectedSize : true) &&
          (v.stock ?? 0) > 0
      );
      return { value, label, disabled: !ok };
    });
  }, [colorLabels, vnorm, selectedSize]);

  useEffect(() => {
    if (sizeLabels.length === 1 && !size) setSize(sizeLabels[0].label);
    if (colorLabels.length === 1 && !color) setColor(colorLabels[0].label);
  }, [sizeLabels, colorLabels, size, color]);

  const currentStock = useMemo(() => {
    if (!p) return 0;
    if (vnorm.length === 0) return Number(p.stock || 0);
    const vv = vnorm.find(
      (v) =>
        (selectedSize ? v.size === selectedSize : true) &&
        (selectedColor ? v.color === selectedColor : true)
    );
    return Number(vv?.stock ?? 0);
  }, [p, vnorm, selectedSize, selectedColor]);

  const variantKey = useMemo(() => `${selectedSize}|${selectedColor}`, [selectedSize, selectedColor]);

  const canAdd =
    !!p &&
    (sizeOptions.length === 0 || !!selectedSize) &&
    (colorOptions.length === 0 || !!selectedColor) &&
    currentStock > 0;

  const effectiveUnitPrice =
    numberFromText(p?.priceTextPromo ?? p?.price) ?? Number(p?.price || 0);

  const addToCart = useCallback(() => {
    if (!p || !canAdd) return;

    addItem({
      productId: p.id,
      variantKey,
      title:
        p.title +
        (variantKey !== "|" ? ` (${size || ""}${size && color ? " · " : ""}${color || ""})` : ""),
      price: effectiveUnitPrice,
      image: p.images?.[0] || "",
      qty,
    });

    try {
      window.dispatchEvent(
        new CustomEvent("toast", { detail: { type: "success", message: `Ajouté : ${p.title}` } })
      );
    } catch {}
  }, [p, canAdd, addItem, variantKey, size, color, qty, effectiveUnitPrice]);

  const canonicalUrl = absUrl(`/p/${slug}`);
  const seoTitle = `${p?.title || ""} | ${SITE_NAME}`;
  const desc = truncate(p?.description || `Achetez ${p?.title} au meilleur prix chez ${SITE_NAME}.`);
  const seoImg = p?.images?.[0] || absUrl("/no-photo.jpg");
  const isInStock = currentStock > 0;

  const productLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Product",
      name: p?.title || "",
      image: Array.isArray(p?.images) && p.images.length ? p.images : [seoImg],
      description: desc,
      sku: p?.code || undefined,
      offers: {
        "@type": "Offer",
        priceCurrency: "XOF",
        price: String((effectiveUnitPrice || 0).toFixed(0)),
        url: canonicalUrl,
        availability: availabilityForSchema(isInStock),
      },
    }),
    [p, seoImg, desc, effectiveUnitPrice, canonicalUrl, isInStock]
  );

  const images = Array.isArray(p?.images) && p.images.length ? p.images : [fallbackImg(1200, 900, "Image")];
  const activeSrc = images[activeImg] || images[0];

  const goPrev = useCallback(() => setActiveImg((i) => (i - 1 + images.length) % images.length), [images.length]);
  const goNext = useCallback(() => setActiveImg((i) => (i + 1) % images.length), [images.length]);

  return (
    <>
      {!loading && p && (
        <Seo
          title={seoTitle}
          description={desc}
          canonical={canonicalUrl}
          openGraph={{ type: "product", siteName: SITE_NAME, title: seoTitle, description: desc, url: canonicalUrl, image: seoImg }}
          twitter={{ card: "summary_large_image", title: seoTitle, description: desc, image: seoImg }}
          jsonLd={[productLd]}
        />
      )}

      {loading ? (
        <div className="d-flex vh-100 align-items-center justify-content-center">
          <div className="spinner-border" role="status" />
        </div>
      ) : !p ? (
        <div className="container py-5">Produit introuvable.</div>
      ) : (
        <main className="container pd-page" itemScope itemType="https://schema.org/Product">
          <div className="pd-bc small">
            <Link to="/" className="text-decoration-none">Accueil</Link>
            <span className="text-muted mx-2">/</span>
            <Link to="/catalogue" className="text-decoration-none">Catalogue</Link>
            <span className="text-muted mx-2">/</span>
            <span className="text-muted">{p.title}</span>
          </div>

          <div className="pd-top">
            <section className="pd-gallery" aria-label="Galerie produit">
              <div className="pd-gallery-inner">
                <div className="pd-thumbs" aria-label="Miniatures">
                  {images.map((u, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`pd-thumb ${i === activeImg ? "active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      title={`Image ${i + 1}`}
                      aria-label={`Image ${i + 1}`}
                    >
                      <img
                        src={u}
                        alt={`thumb-${i}`}
                        onError={(e) => (e.currentTarget.src = fallbackImg(160, 160, "—"))}
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                      />
                    </button>
                  ))}
                </div>

                <div className="pd-stage" aria-label="Image principale">
                  <AlibabaZoom
                    src={activeSrc}
                    alt={p.title}
                    onErrorSrc={fallbackImg(1200, 900)}
                    onPrev={goPrev}
                    onNext={goNext}
                  />

                  {images.length > 1 && (
                    <div className="pd-stage-nav" aria-hidden="true">
                      <button type="button" className="pd-navbtn" onClick={goPrev} title="Précédent">
                        <i className="bi bi-chevron-left" />
                      </button>
                      <button type="button" className="pd-navbtn" onClick={goNext} title="Suivant">
                        <i className="bi bi-chevron-right" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {images.length > 1 && (
                <div className="pd-thumbs-mobile" aria-label="Miniatures mobile">
                  {images.map((u, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`pd-thumb ${i === activeImg ? "active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      aria-label={`Miniature ${i + 1}`}
                      title={`Miniature ${i + 1}`}
                    >
                      <img
                        src={u}
                        alt={`m-thumb-${i}`}
                        onError={(e) => (e.currentTarget.src = fallbackImg(160, 160, "—"))}
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <aside className="pd-buy" aria-label="Achat et variantes">
              <div className="pd-buy-card">
                <h1 className="pd-title h5 m-0">{p.title}</h1>

                <div className="pd-divider" />
                <PriceBlock product={p} />

                <div className="pd-block">
                  <div className="pd-label">Quantité</div>

                  <div className="pd-qty-wide">
                    <button
                      type="button"
                      className="pd-qty-btn"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      aria-label="Diminuer"
                    >
                      −
                    </button>

                    <input
                      className="pd-qty-input"
                      type="number"
                      min={1}
                      max={currentStock ?? 99}
                      value={qty}
                      onChange={(e) =>
                        setQty(Math.max(1, Math.min(Number(e.target.value || 1), currentStock ?? 99)))
                      }
                    />

                    <button
                      type="button"
                      className="pd-qty-btn"
                      onClick={() => setQty((q) => Math.min(currentStock ?? 99, q + 1))}
                      aria-label="Augmenter"
                    >
                      +
                    </button>
                  </div>

                  <div className="pd-stock text-muted small mt-1">
                    {currentStock > 0 ? `Stock: ${currentStock}` : "Rupture de stock"}
                  </div>
                </div>

                <div className="pd-actions">
                  <button className="btn btn-dark" disabled={!canAdd} onClick={addToCart}>
                    Ajouter au panier
                  </button>
                  <Link to="/cart" className="btn btn-outline-dark">
                    Voir le panier
                  </Link>
                </div>

                <div className="pd-perks text-muted small mt-3">
                  <div>✓ Paiement sécurisé Mobile Money / Carte</div>
                  <div>✓ Retour sous 7 jours</div>
                  <div>✓ Livraison 24–48h selon zone</div>
                </div>
              </div>
            </aside>
          </div>
        </main>
      )}
    </>
  );
}

